import { Link, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import { Button } from "@aws-amplify/ui-react";
import Loadingmini from "../components/loadingmini";
import Reservationcommands from "./reservationcommands";
import Reservationactionhistory from "./reservationactionhistory";
import Reservationinsurancecard from "./reservationinsurancecard";
import ReservationCoverage from "../components/reservationcoverage";
import ReservationPayments from "../components/reservationpayments";
import { driverPagePath, fleetFromLocation, fetchStaffSummary, money, platformState } from "../components/staffpage";
import { withFleets } from "../withFleets";
import "../components/staffpage.css";

const SECTIONS = [
  ["actions", "Actions"],
  ["payments", "Payments"],
  ["history", "History"],
  ["insurance", "Insurance"],
  ["json", "JSON"],
];

export default function ReservationNew({ resId: resIdProp }) {
  const params = useParams();
  const location = useLocation();
  const resId = resIdProp || params.resId;
  const fleetCode = fleetFromLocation(location.search).split(",")[0];
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const requestedSection = new URLSearchParams(location.search).get("section") || "";
  const sectionIsKnown = SECTIONS.some(([id]) => id === requestedSection);
  const [section, setSection] = useState(sectionIsKnown ? requestedSection : "actions");
  const [packet, setPacket] = useState(null);
  const [packetError, setPacketError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      if (!fleetCode) {
        setError("Select a fleet, then open this reservation again.");
        setSummary(null);
        return;
      }
      const body = await fetchStaffSummary({ fleetCode, resId });
      if (!body || body.status !== "success") {
        setError((body && body.message) || "Could not load this reservation.");
        setSummary(null);
        return;
      }
      setSummary(body);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [resId, fleetCode]);

  useEffect(() => {
    setSection(sectionIsKnown ? requestedSection : "actions");
  }, [requestedSection, sectionIsKnown, resId]);

  useEffect(() => {
    if (section !== "json") return undefined;
    let cancelled = false;
    setPacket(null);
    setPacketError("");
    async function loadPacket() {
      try {
        const session = await Auth.currentSession();
        const res = await API.get("adminWebTools", "/ago", { queryStringParameters: {
          back_end: process.env.REACT_APP_BACKEND,
          jwt: session.getAccessToken().getJwtToken(),
          api_endpoint: "api3:reservations:get",
          request_type: "GET",
          res_id: resId,
        }});
        if (!cancelled) setPacket(res);
      } catch (e) {
        if (!cancelled) setPacketError(String(e));
      }
    }
    loadPacket();
    return () => { cancelled = true; };
  }, [section, resId]);

  const reservation = summary && summary.reservation;
  const driver = summary && summary.driver;
  const attention = (reservation && reservation.attention) || (driver && driver.attention);
  const platforms = (driver && driver.platforms) || [];
  const resData = reservation ? {
    status: reservation.status,
    start_string: reservation.startString,
    end_string: reservation.endString,
    start: reservation.startUnix,
    end: reservation.endUnix,
    fleet_code: reservation.fleetCode || fleetCode,
    driver_id: reservation.driverId,
    fleet_id: reservation.fleetId,
  } : null;

  return (
    <div className="staff-page">
      <header className="staff-head">
        <p className="staff-kicker">Reservation</p>
        <h1 className="staff-title">{resId}</h1>
        {reservation ? (
          <div className="staff-meta">
            <span>{reservation.status}</span>
            <span>{reservation.startString} – {reservation.endString}</span>
          </div>
        ) : null}
        {attention && attention.sentence ? <p className="staff-sentence">{attention.sentence}</p> : null}
        <div className="staff-groups">
          {reservation ? (
            <section className="staff-group">
              <h2><span className="label">Reservation</span></h2>
              <div className="staff-chips">
                <span className="staff-fact"><span className="k">Status</span><span className="v">{reservation.status}</span></span>
                {money(reservation.grossCost) !== null ? (
                  <span className="staff-fact"><span className="k">Cost</span><span className="v">{money(reservation.grossCost)}</span></span>
                ) : null}
                {reservation.coverage ? (
                  <button type="button" className={"staff-fact wide" + (reservation.coverage.kind === "none" ? " alert" : "")} onClick={() => setSection("insurance")}>
                    <span className="k">Insurance</span>
                    <span className="v">{reservation.coverage.line}</span>
                  </button>
                ) : null}
                {reservation.collectsReservationDeposit && money(reservation.deposit) !== null ? (
                  <span className="staff-fact"><span className="k">Reservation deposit</span><span className="v">{money(reservation.deposit)}</span></span>
                ) : null}
              </div>
            </section>
          ) : null}
          {reservation ? (
            <section className="staff-group">
              <h2>
                <span className="label">Vehicle:</span>
                <Link to={withFleets("/vehicle/" + reservation.carId, fleetCode)}>{reservation.carName || ("Car " + reservation.carId)}</Link>
              </h2>
            </section>
          ) : null}
          {driver ? (
            <section className="staff-group">
              <h2>
                <span className="label">Driver:</span>
                <Link to={driverPagePath(driver.id, fleetCode)}>{driver.name || ("Driver " + driver.id)}</Link>
              </h2>
              <div className="staff-chips">
                {driver.phone ? (
                  <a className="staff-fact" href={"tel:" + driver.phone}><span className="k">Phone</span><span className="v">{driver.phone}</span></a>
                ) : null}
                {money(driver.credits) !== null ? (
                  <Link className="staff-fact" to={driverPagePath(driver.id, fleetCode, "money")}><span className="k">Credits</span><span className="v">{money(driver.credits)}</span></Link>
                ) : null}
                {money(driver.depositOnHand) !== null ? (
                  <Link className="staff-fact" to={driverPagePath(driver.id, fleetCode, "money")}><span className="k">Deposit on hand</span><span className="v">{money(driver.depositOnHand)}</span></Link>
                ) : null}
                {platforms.length ? platforms.map((row) => (
                  <Link className={"staff-fact" + (String(row.verification || "").toLowerCase().includes("verif") ? "" : " alert")} key={row.platform} to={driverPagePath(driver.id, fleetCode, "gig")}>
                    <span className="k">{row.platform || "Platform"}</span>
                    <span className="v">{platformState(row)}</span>
                  </Link>
                )) : driver.platformsReady ? (
                  <Link className="staff-fact alert" to={driverPagePath(driver.id, fleetCode, "gig")}>
                    <span className="k">Gig platform</span>
                    <span className="v">Not linked</span>
                  </Link>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>
        <div className="staff-chips">
          <Button size="small" onClick={load}>Refresh</Button>
        </div>
        {summary && summary.attentionReady === false ? <p className="staff-note">Next step did not load.</p> : null}
        {error ? <p className="staff-note">{error}</p> : null}
      </header>
      {loading ? <Loadingmini /> : reservation ? (
        <>
          <nav className="staff-actions">
            {SECTIONS.map(([id, label]) => (
              <button type="button" key={id} className={section === id ? "on" : ""} onClick={() => setSection(id)}>{label}</button>
            ))}
          </nav>
          <div className="staff-body">
            {reservation.closeCancel || reservation.reason ? (
              <dl className="staff-facts">
                {reservation.closeCancel ? <><dt>Close</dt><dd>{reservation.closeCancel}</dd></> : null}
                {reservation.reason ? <><dt>Reason</dt><dd>{reservation.reason}</dd></> : null}
              </dl>
            ) : null}
            {section === "actions" && resData ? (
              <div className="staff-commands">
                <Reservationcommands
                  resId={resId}
                  resData={resData}
                  refreshParent={load}
                  context={{
                    carName: reservation.carName,
                    status: reservation.status,
                    startString: reservation.startString,
                    endString: reservation.endString,
                  }}
                />
              </div>
            ) : null}
            {section === "payments" ? <ReservationPayments payments={reservation.payments} /> : null}
            {section === "history" ? <Reservationactionhistory resId={resId} /> : null}
            {section === "insurance" ? (
              <>
                <ReservationCoverage
                  coverage={reservation.coverage}
                  driverId={reservation.driverId || (driver && driver.id)}
                  fleetCode={fleetCode}
                />
                <Reservationinsurancecard
                  resId={resId}
                  resStatus={reservation.status}
                  firstCheckFee={reservation.firstCheckFee}
                />
              </>
            ) : null}
            {section === "json" && packetError ? <p className="staff-note">{packetError}</p> : null}
            {section === "json" && !packet && !packetError ? <Loadingmini /> : null}
            {section === "json" && packet ? <pre className="staff-json">{JSON.stringify(packet, null, 2)}</pre> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
